import { useState } from "react";
import {Search, MapPin, Briefcase, Filter, X, Gem, Loader2} from 'lucide-react';
import { mineralCategories } from "@/data/jobs";
import { useJobs } from "@/hooks/useJobs";
import type { JobRecord } from "@/hooks/useJobs";

const experienceFilters = ["All", "Entry Level", "Mid Level", "Senior", "Supervisor"];
const typeFilters = ["All", "FIFO", "Residential", "Remote"];

function JobCard({ job }: { job: JobRecord }) {
  const mineralColors: Record<string, string> = {
    Gold: "#f59e0b",
    Copper: "#b45309",
    Coal: "#9ca3af",
    "Iron Ore": "#dc2626",
    General: "#6b7280",
  };

  const expColors: Record<string, string> = {
    "Entry Level": "#22c55e",
    "Mid Level": "#3b82f6",
    Senior: "#8b5cf6",
    Supervisor: "#f97316",
  };

  return (
    <div className={`bento-card p-5 flex flex-col gap-3 ${job.featured ? "orange-glow" : ""}`} style={job.featured ? { borderColor: "rgba(249,115,22,0.25)" } : {}}>
      {job.featured ? (
        <span className="self-start text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: "rgba(249,115,22,0.15)", color: "#fb923c", fontFamily: "JetBrains Mono, monospace" }}>
          ⭐ FEATURED
        </span>
      ) : null}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: "#2e3850", color: mineralColors[job.mineral] || "#f97316", fontFamily: "JetBrains Mono, monospace" }}>
          {job.logo}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-sm leading-tight truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{job.title}</h3>
          <p className="text-gray-400 text-xs mt-0.5">{job.company}</p>
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0" style={{ background: `${mineralColors[job.mineral]}15`, color: mineralColors[job.mineral], fontFamily: "JetBrains Mono, monospace" }}>
          {job.mineral}
        </span>
      </div>
      <p className="text-gray-400 text-xs leading-relaxed line-clamp-2">{job.description}</p>
      <div className="flex flex-wrap gap-1.5">
        {job.tags.slice(0, 3).map((tag) => (
          <span key={tag} className="text-xs px-2 py-0.5 rounded-md" style={{ background: "#2e3850", color: "#8896b0" }}>{tag}</span>
        ))}
      </div>
      <div className="flex items-center justify-between pt-1 border-t border-white/5">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-xs text-gray-400">
            <MapPin size={11} /> {job.location}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${expColors[job.experience]}15`, color: expColors[job.experience], fontFamily: "JetBrains Mono, monospace" }}>
            {job.experience}
          </span>
        </div>
        <span className="text-xs font-semibold" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>{job.salary.split(" ")[0]}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">{job.posted}</span>
        <button className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all hover:scale-105 hover:opacity-90" style={{ background: "linear-gradient(135deg, #f97316, #ea580c)", color: "white" }}>
          Apply Now
        </button>
      </div>
    </div>
  );
}

export default function JobBoard() {
  const { jobs, loading } = useJobs();
  const [search, setSearch] = useState("");
  const [selectedMineral, setSelectedMineral] = useState("All");
  const [selectedExp, setSelectedExp] = useState("All");
  const [selectedType, setSelectedType] = useState("All");
  const [showFilters, setShowFilters] = useState(false);

  const filtered = jobs.filter((j) => {
    const matchSearch = j.title.toLowerCase().includes(search.toLowerCase()) || j.company.toLowerCase().includes(search.toLowerCase()) || j.location.toLowerCase().includes(search.toLowerCase());
    const matchMineral = selectedMineral === "All" || j.mineral === selectedMineral;
    const matchExp = selectedExp === "All" || j.experience === selectedExp;
    const matchType = selectedType === "All" || j.type === selectedType;
    return matchSearch && matchMineral && matchExp && matchType;
  });

  return (
    <section id="jobs" className="py-20 px-4 sm:px-6 max-w-7xl mx-auto">
      {/* Section Header */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-3">
          <Briefcase size={16} style={{ color: "#f97316" }} />
          <span className="text-sm font-medium" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>// LIVE POSITIONS</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Browse Mining Careers
        </h2>
        <p className="text-gray-400">
          <span style={{ fontFamily: "JetBrains Mono, monospace", color: "#f97316" }}>{jobs.length}</span> active positions — updated daily
        </p>
      </div>

      {/* Mineral Pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        {["All", ...mineralCategories.map((m) => m.name)].map((m) => (
          <button
            key={m}
            onClick={() => setSelectedMineral(m)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all"
            style={
              selectedMineral === m
                ? { background: "#f97316", color: "white" }
                : { background: "#1a1f2e", color: "#8896b0", border: "1px solid rgba(255,255,255,0.06)" }
            }
          >
            {m !== "All" ? <Gem size={12} /> : null}
            {m}
          </button>
        ))}
      </div>

      {/* Search & Filter Bar */}
      <div className="flex gap-3 mb-6">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search jobs, companies, locations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-gray-500 outline-none border border-white/6 focus:border-orange-500/40 transition-colors"
            style={{ background: "#1a1f2e" }}
          />
          {search ? (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
              <X size={14} />
            </button>
          ) : null}
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm border transition-colors"
          style={showFilters ? { background: "rgba(249,115,22,0.15)", borderColor: "rgba(249,115,22,0.4)", color: "#fb923c" } : { background: "#1a1f2e", borderColor: "rgba(255,255,255,0.06)", color: "#8896b0" }}
        >
          <Filter size={14} /> Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters ? (
        <div className="mb-6 p-4 rounded-xl border border-white/6 flex flex-wrap gap-6" style={{ background: "#1a1f2e" }}>
          <div>
            <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider" style={{ fontFamily: "JetBrains Mono, monospace" }}>Experience</p>
            <div className="flex flex-wrap gap-2">
              {experienceFilters.map((f) => (
                <button key={f} onClick={() => setSelectedExp(f)} className="px-3 py-1 rounded-lg text-xs font-medium transition-colors" style={selectedExp === f ? { background: "#f97316", color: "white" } : { background: "#2e3850", color: "#8896b0" }}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider" style={{ fontFamily: "JetBrains Mono, monospace" }}>Work Type</p>
            <div className="flex flex-wrap gap-2">
              {typeFilters.map((f) => (
                <button key={f} onClick={() => setSelectedType(f)} className="px-3 py-1 rounded-lg text-xs font-medium transition-colors" style={selectedType === f ? { background: "#f97316", color: "white" } : { background: "#2e3850", color: "#8896b0" }}>
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* Results count */}
      <p className="text-sm text-gray-500 mb-4" style={{ fontFamily: "JetBrains Mono, monospace" }}>
        {filtered.length === 0 ? "No jobs found" : `${filtered.length} job${filtered.length !== 1 ? "s" : ""} found`}
      </p>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16 bento-card">
          <Loader2 size={28} className="animate-spin" style={{ color: "#f97316" }} />
          <span className="ml-3 text-gray-400 text-sm">Loading positions...</span>
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((job) => (
            <JobCard key={job._id} job={job} />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bento-card">
          <p className="text-4xl mb-3">⛏️</p>
          <p className="text-white font-semibold mb-1">No jobs found</p>
          <p className="text-gray-500 text-sm">Try adjusting your search or filters</p>
        </div>
      )}
    </section>
  );
}
